import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  CheckSquare,
  Clock,
  AlertTriangle,
  AlertOctagon,
  Users,
  Search,
  Filter,
  Plus,
  RefreshCw,
  LayoutGrid,
  List as ListIcon,
  ChevronRight,
  ArrowRight,
  ExternalLink,
  MessageSquare,
  UserCheck,
  Calendar,
  Sparkles,
  CheckCircle2,
  X,
  Send,
  Trash2,
  Layers,
  FileText,
  Truck,
  Calculator,
  ShieldAlert,
  SlidersHorizontal,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  high:   { label: 'High',   bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  medium: { label: 'Medium', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  low:    { label: 'Low',    bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
};

const STATUS_COLUMNS = [
  { id: 'todo',        label: 'To Do',        color: 'border-slate-400', badgeBg: 'bg-slate-100 text-slate-800' },
  { id: 'in_progress', label: 'In Progress',  color: 'border-blue-500',  badgeBg: 'bg-blue-100 text-blue-800' },
  { id: 'review',      label: 'Under Review', color: 'border-amber-500', badgeBg: 'bg-amber-100 text-amber-800' },
  { id: 'completed',   label: 'Completed',    color: 'border-green-500', badgeBg: 'bg-green-100 text-green-800' },
];

const CATEGORIES = [
  { value: 'general',       label: 'General' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'tracking',      label: 'Tracking / SLA' },
  { value: 'pickup',         label: 'Pickup & Dispatch' },
  { value: 'customs',       label: 'Customs Clearance' },
  { value: 'pod',           label: 'Proof of Delivery (POD)' },
  { value: 'billing',       label: 'Billing & Accounts' },
];

export default function TaskManager() {
  const navigate = useNavigate();

  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  // View & Filters
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  const [filterTab, setFilterTab] = useState('all');   // 'all' | 'my' | 'today' | 'overdue' | 'auto'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [reallocateModalOpen, setReallocateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);

  // Form states
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newPriority, setNewPriority] = useState('medium');
  const [newDueDate, setNewDueDate] = useState('');
  const [newDueTime, setNewDueTime] = useState('');
  const [newAssignedTo, setNewAssignedTo] = useState('');
  const [newEntityType, setNewEntityType] = useState('general');
  const [newEntityId, setNewEntityId] = useState('');
  const [newEntityRef, setNewEntityRef] = useState('');

  // Searchable entity lookup states
  const [lookupItems, setLookupItems] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupSearch, setLookupSearch] = useState('');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [manualEntryMode, setManualEntryMode] = useState(false);

  // Reallocate state
  const [reallocateToUser, setReallocateToUser] = useState('');
  const [reallocateRemarks, setReallocateRemarks] = useState('');

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Current logged in user ID
  const currentUserId = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        return u.id || null;
      }
    } catch (e) {}
    return null;
  }, []);

  // Fetch Users
  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/users`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  // Fetch Summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tasks/summary/dashboard`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching task summary:', err);
    }
  }, []);

  // Fetch Tasks
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/tasks`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTasks(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchSummary();
    fetchTasks();
  }, [fetchUsers, fetchSummary, fetchTasks]);

  // Run Auto-Scan
  const handleRunAutoScan = async () => {
    try {
      setScanning(true);
      setScanResult(null);
      const res = await fetch(`${API}/api/tasks/auto-scan`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setScanResult(data);
        await Promise.all([fetchTasks(), fetchSummary()]);
      }
    } catch (err) {
      console.error('Error running auto-scan:', err);
    } finally {
      setScanning(false);
    }
  };


  // Fetch lookup entities when entity type, search, or includeCompleted changes
  const fetchEntityLookup = useCallback(async (type, search, completed) => {
    if (!type || type === 'general') {
      setLookupItems([]);
      return;
    }
    try {
      setLookupLoading(true);
      const params = new URLSearchParams({
        entity_type: type,
        include_completed: completed ? 'true' : 'false',
      });
      if (search && search.trim()) {
        params.set('search', search.trim());
      }
      const res = await fetch(`${API}/api/tasks/lookup/entities?${params}`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setLookupItems(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error in entity lookup:', err);
      setLookupItems([]);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  useEffect(() => {
    if (createModalOpen && newEntityType !== 'general') {
      const timer = setTimeout(() => {
        fetchEntityLookup(newEntityType, lookupSearch, includeCompleted);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [createModalOpen, newEntityType, lookupSearch, includeCompleted, fetchEntityLookup]);

  const handleEntityTypeChange = (type) => {
    setNewEntityType(type);
    setNewEntityId('');
    setNewEntityRef('');
    setLookupSearch('');
    setIncludeCompleted(false);
    setManualEntryMode(false);
    if (type !== 'general') {
      fetchEntityLookup(type, '', false);
    }
  };

  // Create Task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    try {
      const payload = {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        category: newCategory,
        priority: newPriority,
        status: 'todo',
        due_date: newDueDate || null,
        due_time: newDueTime || null,
        assigned_to_id: newAssignedTo && newAssignedTo !== 'none' ? newAssignedTo : null,
        related_entity_type: newEntityType,
        related_entity_id: newEntityId || null,
        related_entity_ref: newEntityRef.trim() || null,
      };

      const res = await fetch(`${API}/api/tasks`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setCreateModalOpen(false);
        setNewTitle('');
        setNewDescription('');
        setNewCategory('general');
        setNewPriority('medium');
        setNewDueDate('');
        setNewDueTime('');
        setNewAssignedTo('');
        setNewEntityType('general');
        setNewEntityId('');
        setNewEntityRef('');
        setLookupSearch('');
        setIncludeCompleted(false);
        setManualEntryMode(false);
        await Promise.all([fetchTasks(), fetchSummary()]);
      }
    } catch (err) {
      console.error('Error creating task:', err);
    }
  };

  // Update Status
  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTasks(prev => prev.map(t => t.id === taskId ? updated : t));
        if (activeTask && activeTask.id === taskId) {
          setActiveTask(updated);
        }
        fetchSummary();
      }
    } catch (err) {
      console.error('Error updating task status:', err);
    }
  };

  // Reallocate Task
  const handleReallocate = async (e) => {
    e.preventDefault();
    if (!activeTask || !reallocateToUser) return;

    try {
      const res = await fetch(`${API}/api/tasks/${activeTask.id}/reallocate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          assigned_to_id: reallocateToUser,
          remarks: reallocateRemarks.trim() || null,
        }),
      });

      if (res.ok) {
        const updated = await res.json();
        setTasks(prev => prev.map(t => t.id === activeTask.id ? updated : t));
        setActiveTask(updated);
        setReallocateModalOpen(false);
        setReallocateRemarks('');
        setReallocateToUser('');
        fetchSummary();
      }
    } catch (err) {
      console.error('Error reallocating task:', err);
    }
  };

  // Add Comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!activeTask || !newComment.trim()) return;

    try {
      setSubmittingComment(true);
      const res = await fetch(
        `${API}/api/tasks/${activeTask.id}/comments?remarks=${encodeURIComponent(newComment.trim())}`,
        {
          method: 'POST',
          headers: authHeaders(),
        }
      );
      if (res.ok) {
        // Refresh active task
        const taskRes = await fetch(`${API}/api/tasks/${activeTask.id}`, { headers: authHeaders() });
        if (taskRes.ok) {
          const freshTask = await taskRes.json();
          setActiveTask(freshTask);
          setTasks(prev => prev.map(t => t.id === freshTask.id ? freshTask : t));
        }
        setNewComment('');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Delete Task
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      const res = await fetch(`${API}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        setDetailModalOpen(false);
        fetchSummary();
      }
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  // Open Details Modal
  const openDetailModal = async (task) => {
    setActiveTask(task);
    setDetailModalOpen(true);
    try {
      const res = await fetch(`${API}/api/tasks/${task.id}`, { headers: authHeaders() });
      if (res.ok) {
        const fullTask = await res.json();
        setActiveTask(fullTask);
      }
    } catch (e) {}
  };

  // Filter Tasks
  const filteredTasks = useMemo(() => {
    const todayStr = new Date().toISOString().split('T')[0];

    return tasks.filter(task => {
      // Filter Tab
      if (filterTab === 'my') {
        if (!currentUserId || task.assigned_to_id !== currentUserId) return false;
      } else if (filterTab === 'today') {
        if (task.due_date !== todayStr || ['completed', 'cancelled'].includes(task.status)) return false;
      } else if (filterTab === 'overdue') {
        if (!task.due_date || task.due_date >= todayStr || ['completed', 'cancelled'].includes(task.status)) return false;
      } else if (filterTab === 'auto') {
        if (task.task_type !== 'auto') return false;
      }

      // Status Filter
      if (statusFilter !== 'all' && task.status !== statusFilter) return false;

      // Priority Filter
      if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;

      // Category Filter
      if (categoryFilter !== 'all' && task.category !== categoryFilter) return false;

      // Assignee Filter
      if (assigneeFilter !== 'all' && task.assigned_to_id !== assigneeFilter) return false;

      // Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = (task.title || '').toLowerCase().includes(q);
        const matchNum = (task.task_number || '').toLowerCase().includes(q);
        const matchRef = (task.related_entity_ref || '').toLowerCase().includes(q);
        const matchUser = (task.assigned_to_name || '').toLowerCase().includes(q);
        if (!matchTitle && !matchNum && !matchRef && !matchUser) return false;
      }

      return true;
    });
  }, [tasks, filterTab, statusFilter, priorityFilter, categoryFilter, assigneeFilter, searchQuery, currentUserId]);

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6 font-ibm">
      {/* ─── Top Header ─── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-5">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md">
              <CheckSquare className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-manrope text-slate-900 tracking-tight flex items-center gap-2">
                Task Management System (TMS)
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  Operations & SLAs
                </Badge>
              </h1>
              <p className="text-sm text-slate-500">
                Coordinate operational tasks, compliance milestones & automated alerts across Dockets, Trips & Estimates.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRunAutoScan}
            disabled={scanning}
            className="flex items-center gap-2 border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 text-blue-600 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Running SLA Scan...' : 'Run SLA & Compliance Scan'}
          </Button>

          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New Task
          </Button>
        </div>
      </div>

      {/* ─── Scan Result Banner (if run) ─── */}
      {scanResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between animate-in fade-in">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <div className="text-sm text-emerald-800">
              <span className="font-semibold">Auto-SLA Scan Completed:</span> Generated{' '}
              <strong>{scanResult.created_auto_tasks}</strong> new alert task(s), auto-resolved{' '}
              <strong>{scanResult.resolved_auto_tasks}</strong> completed task(s), and verified{' '}
              <strong>{scanResult.overdue_tasks_notified}</strong> overdue notifications.
            </div>
          </div>
          <button onClick={() => setScanResult(null)} className="text-emerald-700 hover:text-emerald-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ─── Metric Highlight Cards ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          onClick={() => setFilterTab('overdue')}
          className={`p-4 cursor-pointer transition-all hover:shadow-md border-l-4 border-l-red-500 ${
            filterTab === 'overdue' ? 'ring-2 ring-red-400 bg-red-50/30' : 'bg-white'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Overdue / Expired</span>
            <AlertOctagon className="w-4 h-4 text-red-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold font-manrope text-red-600">
              {summary ? summary.overdue_count : 0}
            </span>
            <span className="text-xs text-red-600 font-medium">Pending action</span>
          </div>
        </Card>

        <Card
          onClick={() => setFilterTab('today')}
          className={`p-4 cursor-pointer transition-all hover:shadow-md border-l-4 border-l-amber-500 ${
            filterTab === 'today' ? 'ring-2 ring-amber-400 bg-amber-50/30' : 'bg-white'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Due Today</span>
            <Clock className="w-4 h-4 text-amber-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold font-manrope text-amber-600">
              {summary ? summary.due_today_count : 0}
            </span>
            <span className="text-xs text-amber-600 font-medium">Scheduled today</span>
          </div>
        </Card>

        <Card
          onClick={() => setFilterTab('my')}
          className={`p-4 cursor-pointer transition-all hover:shadow-md border-l-4 border-l-blue-500 ${
            filterTab === 'my' ? 'ring-2 ring-blue-400 bg-blue-50/30' : 'bg-white'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">My Open Tasks</span>
            <Users className="w-4 h-4 text-blue-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold font-manrope text-blue-600">
              {summary ? summary.my_open_count : 0}
            </span>
            <span className="text-xs text-slate-500">Assigned to you</span>
          </div>
        </Card>

        <Card
          onClick={() => setFilterTab('all')}
          className={`p-4 cursor-pointer transition-all hover:shadow-md border-l-4 border-l-slate-400 ${
            filterTab === 'all' ? 'ring-2 ring-slate-400 bg-slate-50/50' : 'bg-white'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Active</span>
            <Layers className="w-4 h-4 text-slate-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold font-manrope text-slate-900">
              {summary ? summary.total_open_count : 0}
            </span>
            <span className="text-xs text-slate-500">Across company</span>
          </div>
        </Card>
      </div>

      {/* ─── Filter & View Switcher Bar ─── */}
      <Card className="p-4 bg-white shadow-sm space-y-4">
        {/* Row 1: Quick Filter Tabs + View Mode Toggle */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b pb-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { id: 'all',     label: 'All Tasks' },
              { id: 'my',      label: 'My Tasks' },
              { id: 'today',   label: '⚡ Due Today' },
              { id: 'overdue', label: '🚨 Overdue / Expired' },
              { id: 'auto',    label: '🤖 Auto-SLA Alerts' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilterTab(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  filterTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 self-end">
            <div className="border rounded-lg p-0.5 flex bg-slate-100">
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'kanban'
                    ? 'bg-white text-blue-700 shadow-sm font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Kanban
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'list'
                    ? 'bg-white text-blue-700 shadow-sm font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <ListIcon className="w-3.5 h-3.5" />
                List
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Search & Detailed Dropdown Selectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by title, task #, docket or assignee..."
              className="pl-9 h-9 text-xs"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="todo">To Do</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="review">Under Review</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue placeholder="All Assignees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* ─── Tasks Display: Kanban or List ─── */}
      {loading ? (
        <div className="py-20 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
          Loading tasks and milestones...
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="py-16 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-white space-y-3">
          <CheckSquare className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="text-base font-semibold text-slate-700 font-manrope">No tasks found</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {searchQuery || filterTab !== 'all'
              ? 'No tasks match your current filter criteria. Try clearing search or resetting filters.'
              : 'All caught up! No operational tasks are currently pending.'}
          </p>
          <Button
            size="sm"
            onClick={() => setCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Create First Task
          </Button>
        </div>
      ) : viewMode === 'kanban' ? (
        /* ─── KANBAN BOARD VIEW ─── */
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
          {STATUS_COLUMNS.map(col => {
            const colTasks = filteredTasks.filter(t => t.status === col.id);
            return (
              <div
                key={col.id}
                className="bg-slate-50/80 rounded-xl border border-slate-200/80 p-3 flex flex-col min-h-[500px]"
              >
                {/* Column Header */}
                <div className="flex items-center justify-between pb-3 border-b mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${col.badgeBg}`} />
                    <span className="font-semibold text-xs text-slate-800 uppercase tracking-wider font-manrope">
                      {col.label}
                    </span>
                  </div>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-700">
                    {colTasks.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="space-y-3 flex-1 overflow-y-auto">
                  {colTasks.map(task => {
                    const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                    const isOverdue = task.due_date && task.due_date < todayStr && task.status !== 'completed';
                    const isToday = task.due_date === todayStr && task.status !== 'completed';

                    return (
                      <Card
                        key={task.id}
                        onClick={() => openDetailModal(task)}
                        className={`p-3 bg-white hover:shadow-md transition-all cursor-pointer border border-slate-200 relative group ${
                          isOverdue ? 'border-red-300 ring-1 ring-red-200' : ''
                        }`}
                      >
                        {/* Top: Task No & Priority */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-mono font-bold text-slate-600">
                            {task.task_number}
                          </span>
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${prio.bg} ${prio.text} ${prio.border}`}
                          >
                            {prio.label}
                          </span>
                        </div>

                        {/* Title */}
                        <h4 className="text-xs font-semibold text-slate-900 font-manrope line-clamp-2 mb-1.5">
                          {task.title}
                        </h4>

                        {/* Description Preview */}
                        {task.description && (
                          <p className="text-[11px] text-slate-500 line-clamp-2 mb-2 font-ibm">
                            {task.description}
                          </p>
                        )}

                        {/* Connected Entity Tag */}
                        {task.related_entity_ref && (
                          <div className="mb-2.5">
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                              {task.related_entity_type === 'docket' && <FileText className="w-3 h-3 text-purple-600" />}
                              {task.related_entity_type === 'trip' && <Truck className="w-3 h-3 text-blue-600" />}
                              {task.related_entity_type === 'estimate' && <Calculator className="w-3 h-3 text-emerald-600" />}
                              <span className="uppercase text-[9px] text-slate-400 font-bold">{task.related_entity_type}:</span>
                              <span className="font-mono">{task.related_entity_ref}</span>
                            </span>
                          </div>
                        )}

                        {/* Footer: Assignee & Due Date */}
                        <div className="pt-2 border-t flex items-center justify-between text-[11px]">
                          <div className="flex items-center gap-1.5 text-slate-600 truncate max-w-[120px]">
                            <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[9px]">
                              {(task.assigned_to_name || 'U').charAt(0)}
                            </div>
                            <span className="truncate">{task.assigned_to_name || 'Unassigned'}</span>
                          </div>

                          {task.due_date && (
                            <span
                              className={`flex items-center gap-1 font-semibold ${
                                isOverdue
                                  ? 'text-red-600'
                                  : isToday
                                  ? 'text-amber-600'
                                  : 'text-slate-500'
                              }`}
                            >
                              {isOverdue && <AlertOctagon className="w-3 h-3 text-red-600" />}
                              {isToday && <Clock className="w-3 h-3 text-amber-600" />}
                              {task.due_date}
                            </span>
                          )}
                        </div>

                        {/* Quick Move Status Buttons (Hover) */}
                        <div
                          onClick={e => e.stopPropagation()}
                          className="mt-2.5 pt-2 border-t border-dashed flex items-center justify-between gap-1 text-[10px]"
                        >
                          <span className="text-[10px] text-slate-400">Move to:</span>
                          <div className="flex items-center gap-1">
                            {col.id !== 'todo' && (
                              <button
                                onClick={() => handleUpdateStatus(task.id, 'todo')}
                                className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                              >
                                To Do
                              </button>
                            )}
                            {col.id !== 'in_progress' && (
                              <button
                                onClick={() => handleUpdateStatus(task.id, 'in_progress')}
                                className="px-1.5 py-0.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                              >
                                Progress
                              </button>
                            )}
                            {col.id !== 'review' && (
                              <button
                                onClick={() => handleUpdateStatus(task.id, 'review')}
                                className="px-1.5 py-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-700"
                              >
                                Review
                              </button>
                            )}
                            {col.id !== 'completed' && (
                              <button
                                onClick={() => handleUpdateStatus(task.id, 'completed')}
                                className="px-1.5 py-0.5 rounded bg-green-100 hover:bg-green-200 text-green-700 font-semibold"
                              >
                                Done ✓
                              </button>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ─── LIST / TABLE VIEW ─── */
        <Card className="overflow-hidden bg-white shadow-sm border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b text-slate-500 font-semibold uppercase tracking-wider text-[11px]">
                  <th className="py-3 px-4">Task #</th>
                  <th className="py-3 px-4">Title & Context</th>
                  <th className="py-3 px-4">Related Entity</th>
                  <th className="py-3 px-4">Priority</th>
                  <th className="py-3 px-4">Assignee</th>
                  <th className="py-3 px-4">Due Date</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-ibm">
                {filteredTasks.map(task => {
                  const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                  const isOverdue = task.due_date && task.due_date < todayStr && task.status !== 'completed';
                  const isToday = task.due_date === todayStr && task.status !== 'completed';

                  return (
                    <tr
                      key={task.id}
                      onClick={() => openDetailModal(task)}
                      className={`hover:bg-slate-50/80 cursor-pointer transition-colors ${
                        isOverdue ? 'bg-red-50/20' : ''
                      }`}
                    >
                      <td className="py-3 px-4 font-mono font-bold text-slate-700">
                        {task.task_number}
                        {task.task_type === 'auto' && (
                          <Badge variant="outline" className="ml-1.5 text-[9px] bg-purple-50 text-purple-700 border-purple-200">
                            Auto
                          </Badge>
                        )}
                      </td>

                      <td className="py-3 px-4 max-w-sm">
                        <div className="font-semibold text-slate-900 font-manrope">{task.title}</div>
                        {task.description && (
                          <div className="text-[11px] text-slate-500 truncate mt-0.5">{task.description}</div>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        {task.related_entity_ref ? (
                          <span className="inline-flex items-center gap-1 font-mono text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-800">
                            <span className="uppercase text-[9px] text-slate-400 font-bold">{task.related_entity_type}:</span>
                            {task.related_entity_ref}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${prio.bg} ${prio.text} ${prio.border}`}>
                          {prio.label}
                        </span>
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[9px]">
                            {(task.assigned_to_name || 'U').charAt(0)}
                          </div>
                          <span className="font-medium text-slate-700">{task.assigned_to_name || 'Unassigned'}</span>
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        {task.due_date ? (
                          <span
                            className={`font-semibold flex items-center gap-1 ${
                              isOverdue
                                ? 'text-red-600'
                                : isToday
                                ? 'text-amber-600'
                                : 'text-slate-600'
                            }`}
                          >
                            {isOverdue && <AlertOctagon className="w-3.5 h-3.5 text-red-600" />}
                            {isToday && <Clock className="w-3.5 h-3.5 text-amber-600" />}
                            {task.due_date}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                        <Select
                          value={task.status}
                          onValueChange={val => handleUpdateStatus(task.id, val)}
                        >
                          <SelectTrigger className="h-7 text-[11px] w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todo">To Do</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="review">Review</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>

                      <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setActiveTask(task);
                              setReallocateToUser(task.assigned_to_id || '');
                              setReallocateModalOpen(true);
                            }}
                            title="Reallocate Task"
                            className="h-7 px-2 text-slate-600 hover:text-blue-600"
                          >
                            <UserCheck className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetailModal(task)}
                            title="View History / Comment"
                            className="h-7 px-2 text-slate-600 hover:text-blue-600"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ─── MODAL: CREATE TASK ─── */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-xl font-ibm">
          <DialogHeader>
            <DialogTitle className="font-manrope text-lg flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              Create New Operational Task
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateTask} className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Task Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="e.g., Collect invoice and packing list from shipper"
                required
                className="h-9 text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Category</label>
                <Select value={newCategory} onValueChange={setNewCategory}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Priority</label>
                <Select value={newPriority} onValueChange={setNewPriority}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Due Date</label>
                <Input
                  type="date"
                  value={newDueDate}
                  onChange={e => setNewDueDate(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Due Time (Optional)</label>
                <Input
                  type="time"
                  value={newDueTime}
                  onChange={e => setNewDueTime(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Assigned Team Member</label>
                <Select value={newAssignedTo} onValueChange={setNewAssignedTo}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Select user..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Related Entity Type</label>
                <Select value={newEntityType} onValueChange={handleEntityTypeChange}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (No entity)</SelectItem>
                    <SelectItem value="docket">Docket (Job)</SelectItem>
                    <SelectItem value="trip">Trip / Dispatch</SelectItem>
                    <SelectItem value="estimate">Estimate / Quote</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ─── Searchable & Filterable Entity Picker ─── */}
            {newEntityType !== 'general' && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5 font-manrope">
                    {newEntityType === 'docket' && <FileText className="w-3.5 h-3.5 text-purple-600" />}
                    {newEntityType === 'trip' && <Truck className="w-3.5 h-3.5 text-blue-600" />}
                    {newEntityType === 'estimate' && <Calculator className="w-3.5 h-3.5 text-emerald-600" />}
                    {newEntityType === 'customer' && <Users className="w-3.5 h-3.5 text-orange-600" />}
                    Select {newEntityType === 'docket' ? 'Docket (Job)' : newEntityType === 'trip' ? 'Trip' : newEntityType === 'estimate' ? 'Estimate' : 'Customer'}
                  </label>

                  {/* Toggle: Include Completed items */}
                  {(newEntityType === 'docket' || newEntityType === 'trip') && !manualEntryMode && (
                    <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-slate-600 select-none hover:text-slate-900">
                      <input
                        type="checkbox"
                        checked={includeCompleted}
                        onChange={e => setIncludeCompleted(e.target.checked)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-0 w-3.5 h-3.5"
                      />
                      <span>Include Completed / Delivered</span>
                    </label>
                  )}
                </div>

                {manualEntryMode ? (
                  /* Manual input fallback */
                  <div className="space-y-1">
                    <Input
                      value={newEntityRef}
                      onChange={e => setNewEntityRef(e.target.value)}
                      placeholder={`Type reference manually (e.g. ${newEntityType === 'docket' ? 'BOM-2026-004' : newEntityType === 'trip' ? 'TRIP-0012' : 'EST-001'})...`}
                      className="h-8 text-xs font-mono bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setManualEntryMode(false)}
                      className="text-[10px] text-blue-600 hover:underline pt-0.5"
                    >
                      ← Back to searchable list
                    </button>
                  </div>
                ) : newEntityRef ? (
                  /* Selected Item Pill / Card */
                  <div className="flex items-center justify-between p-2.5 bg-white border border-blue-200 rounded-lg shadow-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">
                        ✓
                      </div>
                      <div>
                        <div className="font-mono font-bold text-xs text-slate-900">{newEntityRef}</div>
                        <div className="text-[10px] text-slate-500 capitalize">{newEntityType} attached</div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setNewEntityId('');
                        setNewEntityRef('');
                      }}
                      className="h-7 px-2 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      Change
                    </Button>
                  </div>
                ) : (
                  /* Search Input + Scrollable List of Entities */
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                      <Input
                        value={lookupSearch}
                        onChange={e => setLookupSearch(e.target.value)}
                        placeholder={`Search ${newEntityType}s by number, vehicle, destination...`}
                        className="pl-8 h-8 text-xs bg-white"
                      />
                      {lookupSearch && (
                        <button
                          type="button"
                          onClick={() => setLookupSearch('')}
                          className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Scrollable list */}
                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg bg-white divide-y divide-slate-100">
                      {lookupLoading ? (
                        <div className="p-3 text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-600" />
                          Loading {newEntityType}s...
                        </div>
                      ) : lookupItems.length === 0 ? (
                        <div className="p-3 text-center text-xs text-slate-500">
                          No {newEntityType}s found.{' '}
                          {!includeCompleted && (newEntityType === 'docket' || newEntityType === 'trip') && (
                            <button
                              type="button"
                              onClick={() => setIncludeCompleted(true)}
                              className="text-blue-600 font-semibold hover:underline"
                            >
                              Check completed {newEntityType}s?
                            </button>
                          )}
                        </div>
                      ) : (
                        lookupItems.map(item => (
                          <div
                            key={item.id}
                            onClick={() => {
                              setNewEntityId(item.id);
                              setNewEntityRef(item.ref);
                            }}
                            className="p-2 hover:bg-blue-50/70 cursor-pointer transition-colors flex items-center justify-between text-xs"
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-slate-800">{item.ref}</span>
                                {item.is_completed && (
                                  <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-600 border-slate-200">
                                    Completed
                                  </Badge>
                                )}
                              </div>
                              {item.sub && (
                                <div className="text-[10px] text-slate-500 truncate max-w-xs">{item.sub}</div>
                              )}
                            </div>
                            <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                              Select +
                            </span>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => setManualEntryMode(true)}
                        className="text-[10px] text-slate-400 hover:text-slate-600 underline"
                      >
                        Can't find it? Enter reference manually
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Description & Notes</label>
              <textarea
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
                rows={3}
                placeholder="Add any specific instructions or requirements..."
                className="w-full text-xs p-2.5 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 font-ibm"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                Create & Assign Task
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── MODAL: REALLOCATE TASK ─── */}
      <Dialog open={reallocateModalOpen} onOpenChange={setReallocateModalOpen}>
        <DialogContent className="max-w-md font-ibm">
          <DialogHeader>
            <DialogTitle className="font-manrope text-base flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-amber-600" />
              Reallocate Task to Colleague
            </DialogTitle>
          </DialogHeader>

          {activeTask && (
            <form onSubmit={handleReallocate} className="space-y-4 pt-2">
              <div className="p-3 bg-slate-50 rounded-lg text-xs space-y-1">
                <div className="font-bold text-slate-800">{activeTask.task_number}: {activeTask.title}</div>
                <div className="text-slate-500">
                  Currently assigned to: <strong>{activeTask.assigned_to_name || 'Unassigned'}</strong>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Transfer to Team Member <span className="text-red-500">*</span>
                </label>
                <Select value={reallocateToUser} onValueChange={setReallocateToUser} required>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Select new assignee..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Hand-off Remarks / Instructions
                </label>
                <textarea
                  value={reallocateRemarks}
                  onChange={e => setReallocateRemarks(e.target.value)}
                  rows={2}
                  placeholder="Reason for transfer or instructions for the new assignee..."
                  className="w-full text-xs p-2.5 border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 font-ibm"
                />
              </div>

              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setReallocateModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                  Confirm Reallocation
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── MODAL: TASK DETAIL & AUDIT TIMELINE DRAWER ─── */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto font-ibm">
          {activeTask && (
            <div className="space-y-5">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-800">
                      {activeTask.task_number}
                    </span>
                    <Badge variant="outline" className="text-xs uppercase">
                      {activeTask.category}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteTask(activeTask.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
                <DialogTitle className="font-manrope text-lg pt-2 text-slate-900">
                  {activeTask.title}
                </DialogTitle>
              </DialogHeader>

              {/* Status and Action Buttons */}
              <div className="p-3 bg-slate-50 rounded-xl flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-semibold">Status:</span>
                  <Select
                    value={activeTask.status}
                    onValueChange={val => handleUpdateStatus(activeTask.id, val)}
                  >
                    <SelectTrigger className="h-8 text-xs font-semibold w-[130px] bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">To Do</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="review">Under Review</SelectItem>
                      <SelectItem value="completed">Completed ✓</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setReallocateToUser(activeTask.assigned_to_id || '');
                      setReallocateModalOpen(true);
                    }}
                    className="h-8 text-xs flex items-center gap-1.5"
                  >
                    <UserCheck className="w-3.5 h-3.5 text-blue-600" />
                    Reallocate
                  </Button>
                </div>
              </div>

              {/* Meta Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="p-2.5 border rounded-lg bg-white">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Priority</span>
                  <span className="font-semibold text-slate-800 capitalize">{activeTask.priority}</span>
                </div>

                <div className="p-2.5 border rounded-lg bg-white">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Due Date</span>
                  <span className="font-semibold text-slate-800">{activeTask.due_date || 'No deadline'}</span>
                </div>

                <div className="p-2.5 border rounded-lg bg-white">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Assignee</span>
                  <span className="font-semibold text-slate-800 truncate block">
                    {activeTask.assigned_to_name || 'Unassigned'}
                  </span>
                </div>

                <div className="p-2.5 border rounded-lg bg-white">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Connected Entity</span>
                  <span className="font-semibold text-slate-800 truncate block">
                    {activeTask.related_entity_ref || 'None'}
                  </span>
                </div>
              </div>

              {/* Description */}
              {activeTask.description && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block">
                    Description
                  </span>
                  <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {activeTask.description}
                  </div>
                </div>
              )}

              {/* Connected Entity Link Button */}
              {activeTask.related_entity_ref && (
                <div className="p-3 border border-blue-100 bg-blue-50/50 rounded-xl flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                    <span>
                      Connected to <strong>{activeTask.related_entity_type.toUpperCase()}</strong>: {activeTask.related_entity_ref}
                    </span>
                  </div>
                  {activeTask.related_entity_type === 'docket' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs bg-white text-blue-700 hover:bg-blue-50"
                      onClick={() => {
                        navigate('/dockets');
                        setDetailModalOpen(false);
                      }}
                    >
                      Go to Dockets
                    </Button>
                  )}
                  {activeTask.related_entity_type === 'trip' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs bg-white text-blue-700 hover:bg-blue-50"
                      onClick={() => {
                        navigate('/fleet/trips');
                        setDetailModalOpen(false);
                      }}
                    >
                      Go to Trips
                    </Button>
                  )}
                  {activeTask.related_entity_type === 'estimate' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs bg-white text-blue-700 hover:bg-blue-50"
                      onClick={() => {
                        navigate('/estimates');
                        setDetailModalOpen(false);
                      }}
                    >
                      Go to Estimates
                    </Button>
                  )}
                </div>
              )}

              {/* Activity Timeline & Logs */}
              <div className="space-y-3 pt-2">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block">
                  Activity Timeline & Comments ({activeTask.logs ? activeTask.logs.length : 0})
                </span>

                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {activeTask.logs && activeTask.logs.length > 0 ? (
                    activeTask.logs.map((log, idx) => (
                      <div key={log.id || idx} className="p-2.5 bg-slate-50 rounded-lg text-xs flex items-start gap-2.5">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                        <div className="flex-1 space-y-0.5">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-slate-800">
                              {log.from_user_name || 'System'}
                            </span>
                            <span className="text-[10px] text-slate-400">
                              {new Date(log.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-slate-600 text-[11px]">{log.remarks}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-400 text-center py-4">No audit logs recorded yet.</div>
                  )}
                </div>

                {/* Add Comment Form */}
                <form onSubmit={handleAddComment} className="flex gap-2 pt-1">
                  <Input
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Add an internal comment or update remark..."
                    className="h-9 text-xs"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={submittingComment || !newComment.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />
                    Comment
                  </Button>
                </form>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
